import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Row, Space, Typography, message } from 'antd';
import { FolderOpenOutlined, ReloadOutlined } from '@ant-design/icons';

import RootFolderSelect from '../../components/modals/RootFolderSelect';
import MhiDocsIndexTable from './DccIndexTableComponents/MhiDocsIndexTable';
import DccCorrespondenceIndexTable from './DccIndexTableComponents/DccCorrespondenceIndexTable';
import { dccIndexRefresh, dccPdfStatsRefresh } from '../../services/dccApi';
import { useAuth } from '../../components/common/security/AuthContext';

const { Title, Paragraph, Text } = Typography;

const DccIndexTables: React.FC = () => {
  const { token } = useAuth();
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [refreshingFileIndex, setRefreshingFileIndex] = useState(false);
  const [refreshingPdfStats, setRefreshingPdfStats] = useState(false);
  const isMhiDocsFolder = useMemo(() => {
    const f = String(selectedFolder || '').replace(/\\/g, '/').replace(/^\/+/, '');
    if (!f) return false;
    return f.split('/')[0].toLowerCase() === 'mhidocmails';
  }, [selectedFolder]);
  const isCorrespondencesFolder = useMemo(() => {
    const f = String(selectedFolder || '').replace(/\\/g, '/').replace(/^\/+/, '');
    if (!f) return false;
    const root = (f.split('/')[0] || '').toLowerCase();
    return root === 'correspondences' || root === 'correspondence';
  }, [selectedFolder]);

  const handleRefreshAllFileIndex = async () => {
    if (!token) {
      message.error('No session');
      return;
    }
    try {
      setRefreshingFileIndex(true);
      await dccIndexRefresh({ root: 'ALL' }, token);
      message.success('File index refresh started');
    } catch (e: any) {
      message.error(e?.message || 'Failed to refresh file index');
    } finally {
      setRefreshingFileIndex(false);
    }
  };

  const handleRefreshPdfStats = async () => {
    if (!token) {
      message.error('No session');
      return;
    }
    try {
      setRefreshingPdfStats(true);
      const result = await dccPdfStatsRefresh({ root: 'ALL', path: '', recursive: true }, token);
      message.success(
        `PDF stats refresh done (written=${result.written ?? 0}, pruned=${result.pruned ?? 0}, ${result.elapsed_ms ?? 0}ms)`
      );
    } catch (e: any) {
      message.error(e?.message || 'Failed to refresh PDF stats');
    } finally {
      setRefreshingPdfStats(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle" wrap gutter={[16, 16]}>
          <Col>
            <Title level={2} style={{ marginBottom: selectedFolder ? 4 : 0 }}>
              DCC Folder Index Tables
            </Title>
            {selectedFolder && (
              <Text type="secondary">
                Selected folder:
                {' '}
                <Text code>{selectedFolder}</Text>
              </Text>
            )}
          </Col>
          <Col>
            <Space direction="horizontal" size="middle" wrap>
              <Button
                type="primary"
                icon={<FolderOpenOutlined />}
                onClick={() => setIsFolderModalOpen(true)}
              >
                Select DCC Folder
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefreshAllFileIndex}
                loading={refreshingFileIndex}
              >
                Refresh File Index (All)
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefreshPdfStats}
                loading={refreshingPdfStats}
              >
                Refresh PDF Stats
              </Button>
            </Space>
          </Col>
        </Row>

        {selectedFolder && !isMhiDocsFolder && !isCorrespondencesFolder && (
          <Alert
            type="info"
            showIcon
            message="Folder selected"
            description="Index table listing for this folder is not implemented yet."
          />
        )}

        {/* MHIDOCMAILS index table */}
        <MhiDocsIndexTable selectedFolder={selectedFolder} />

        {/* Correspondences index table */}
        <DccCorrespondenceIndexTable selectedFolder={selectedFolder} />
      </Space>

      <RootFolderSelect
        open={isFolderModalOpen}
        basePath=""
        onCancel={() => setIsFolderModalOpen(false)}
        onSelect={(folderPath) => {
          setSelectedFolder(folderPath);
          setIsFolderModalOpen(false);
        }}
      />
    </div>
  );
};

export default DccIndexTables;
